import React from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Eye, FileText, Tag, User } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { getFileKindIcon } from '@utils/fileKind';
import { formatBytes, formatDistanceToNow } from '@utils/formatters';
import { isDisplayableLabel } from '@utils/labels';
import type { SearchResult } from '../../types';
import { cn } from '@/lib/utils';
import { renderHighlightedText } from './HighlightedText';

const MAX_TAGS = 5;

interface SearchResultCardProps {
  result: SearchResult;
  onSelect: (result: SearchResult) => void;
}

const Meta: React.FC<{ icon: React.ReactNode; children: React.ReactNode }> = ({
  icon,
  children,
}) => (
  <span className="flex items-center gap-1 text-xs text-muted-foreground">
    {icon}
    <span className="revy-cjk truncate">{children}</span>
  </span>
);

const SearchResultCard: React.FC<SearchResultCardProps> = ({ result, onSelect }) => {
  const { t } = useTranslation();
  const Icon = getFileKindIcon(result.mimeType);

  return (
    <Card
      onClick={() => onSelect(result)}
      className={cn(
        'flex cursor-pointer gap-3 p-4 transition-[box-shadow,border-color,translate] duration-200 ease-shell',
        'shadow-[0_1px_2px_rgb(0_0_0/0.04)] hover:-translate-y-px hover:border-primary/25',
        'hover:shadow-[0_1px_2px_rgb(0_0_0/0.04),0_10px_28px_rgb(0_0_0/0.08)]',
        'motion-reduce:transition-none motion-reduce:hover:translate-y-0',
      )}
    >
      <span className="mt-0.5 grid size-9 shrink-0 place-items-center rounded-lg bg-muted text-muted-foreground">
        <Icon className="size-[18px]" strokeWidth={1.8} />
      </span>

      <div className="min-w-0 flex-1">
        <h3 className="revy-cjk line-clamp-2 text-base font-medium leading-[1.5] text-primary">
          {renderHighlightedText(result.highlights?.title || result.title || t('search.untitledResult'))}
        </h3>

        {(result.highlights?.description || result.description) && (
          <p className="revy-cjk mt-1 line-clamp-2 text-sm leading-[1.6] text-muted-foreground">
            {renderHighlightedText(result.highlights?.description || result.description || '')}
          </p>
        )}

        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
          {isDisplayableLabel(result.category) && (
            <Meta icon={<FileText className="size-3.5 shrink-0" strokeWidth={1.9} />}>
              {result.category}
            </Meta>
          )}
          {isDisplayableLabel(result.uploadedBy) && (
            <Meta icon={<User className="size-3.5 shrink-0" strokeWidth={1.9} />}>
              {result.uploadedBy}
            </Meta>
          )}
          {result.uploadedAt && (
            <Meta icon={<Calendar className="size-3.5 shrink-0" strokeWidth={1.9} />}>
              {t('time.ago', { value: formatDistanceToNow(new Date(result.uploadedAt)) })}
            </Meta>
          )}
          {Boolean(result.fileSize) && (
            <span className="revy-figure text-xs text-muted-foreground">
              {formatBytes(result.fileSize ?? 0)}
            </span>
          )}
        </div>

        {result.tags && result.tags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {result.tags.slice(0, MAX_TAGS).map((tag) => (
              <Badge key={tag} variant="outline" className="gap-1 font-normal">
                <Tag className="size-3" strokeWidth={2} />
                {tag}
              </Badge>
            ))}
            {result.tags.length > MAX_TAGS && (
              <Badge variant="secondary" className="font-normal">
                {t('common.moreCount', { count: result.tags.length - MAX_TAGS })}
              </Badge>
            )}
          </div>
        )}

        <Button
          variant="ghost"
          size="sm"
          className="-ml-2 mt-2 h-8"
          onClick={(event) => {
            event.stopPropagation();
            onSelect(result);
          }}
        >
          <Eye className="size-3.5" strokeWidth={1.9} />
          {t('common.view')}
        </Button>
      </div>
    </Card>
  );
};

export default SearchResultCard;
